import type { Basic, Organization } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';

import { resolvePracticeOrganization } from '../../../../lib/practiceOrganization';
import {
  TEMPLATE_SETTINGS_CODE,
  TEMPLATE_SETTINGS_DATA_EXT_URL,
  TEMPLATE_SETTINGS_SYSTEM,
  type DocumentationSettings,
} from '../types';

export interface LoadedDocumentationSettings {
  settingsJson: string | null;
  organizationId: string | null;
  legacyBasicId: string | null;
}

function getSettingsJsonFromOrganization(org: Organization | undefined): string | null {
  const ext = org?.extension?.find((e) => e.url === TEMPLATE_SETTINGS_DATA_EXT_URL);
  return ext?.valueString ?? null;
}

function withSettingsExtension(org: Organization, json: string): Organization {
  const otherExtensions = (org.extension ?? []).filter((e) => e.url !== TEMPLATE_SETTINGS_DATA_EXT_URL);
  return {
    ...org,
    extension: [...otherExtensions, { url: TEMPLATE_SETTINGS_DATA_EXT_URL, valueString: json }],
  };
}

async function loadLegacyBasicSettings(medplum: MedplumClient): Promise<{
  json: string | null;
  basicId: string | null;
}> {
  try {
    const bundle = await medplum.search('Basic', {
      code: `${TEMPLATE_SETTINGS_SYSTEM}|${TEMPLATE_SETTINGS_CODE}`,
      _count: '1',
    });
    const resource = bundle.entry?.[0]?.resource as Basic | undefined;
    if (!resource) return { json: null, basicId: null };
    const ext = resource.extension?.find((e) => e.url === TEMPLATE_SETTINGS_DATA_EXT_URL);
    return { json: ext?.valueString ?? null, basicId: resource.id ?? null };
  } catch {
    return { json: null, basicId: null };
  }
}

/** Load raw settings JSON from the linked practice Organization, with Basic fallback. */
export async function loadDocumentationSettingsRaw(
  medplum: MedplumClient,
  organization?: Organization | null,
): Promise<LoadedDocumentationSettings> {
  const resolvedOrganization = organization ?? (await resolvePracticeOrganization(medplum));
  const organizationId = resolvedOrganization?.id ?? null;

  const orgJson = getSettingsJsonFromOrganization(resolvedOrganization ?? undefined);
  if (orgJson) {
    return {
      settingsJson: orgJson,
      organizationId,
      legacyBasicId: null,
    };
  }

  const legacy = await loadLegacyBasicSettings(medplum);
  return {
    settingsJson: legacy.json,
    organizationId,
    legacyBasicId: legacy.basicId,
  };
}

/** Persist settings JSON on the practice Organization resource. */
export async function saveDocumentationSettingsRaw(
  medplum: MedplumClient,
  organizationId: string,
  json: string,
  legacyBasicId?: string | null,
): Promise<Organization> {
  const organization = await medplum.readResource('Organization', organizationId);
  const updated = withSettingsExtension(organization, json);
  const saved = await medplum.updateResource(updated);

  if (legacyBasicId) {
    try {
      await medplum.deleteResource('Basic', legacyBasicId);
    } catch {
      // Non-fatal: legacy Basic may already be removed or inaccessible.
    }
  }

  return saved;
}

export type { DocumentationSettings };
